const MIN_SPECTRUM_PAIRS = 12;
const MAX_SPECTRUM_PAIRS = 40;
const SPECTRUM_PAIR_WIDTH = 8;
const SPECTRUM_PAIR_GAP = 6;

const clampLevel = (level: number): number =>
  Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0;

export const spectrumBandCountForWidth = (width: number): number => {
  const availableWidth = Number.isFinite(width) ? Math.max(0, width) : 0;
  const fittingPairs = Math.floor(
    (availableWidth + SPECTRUM_PAIR_GAP) /
      (SPECTRUM_PAIR_WIDTH + SPECTRUM_PAIR_GAP),
  );
  return Math.min(
    MAX_SPECTRUM_PAIRS,
    Math.max(MIN_SPECTRUM_PAIRS, fittingPairs),
  );
};

export const resampleSpectrum = (
  source: readonly number[] | null | undefined,
  targetCount: number,
  fallbackLevel: number,
): number[] => {
  const count = Number.isFinite(targetCount)
    ? Math.max(0, Math.floor(targetCount))
    : 0;
  if (count === 0) return [];
  if (source == null || source.length === 0) {
    return Array(count).fill(clampLevel(fallbackLevel));
  }
  if (source.length === 1) return Array(count).fill(clampLevel(source[0]));

  if (count >= source.length) {
    return Array.from({ length: count }, (_, index) => {
      const position = (index * (source.length - 1)) / Math.max(1, count - 1);
      const lowerIndex = Math.floor(position);
      const upperIndex = Math.min(source.length - 1, lowerIndex + 1);
      const ratio = position - lowerIndex;
      const lower = clampLevel(source[lowerIndex]);
      const upper = clampLevel(source[upperIndex]);
      return lower + (upper - lower) * ratio;
    });
  }

  return Array.from({ length: count }, (_, index) => {
    const start = Math.floor((index * source.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((index + 1) * source.length) / count),
    );
    let peak = 0;
    for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) {
      peak = Math.max(peak, clampLevel(source[sourceIndex]));
    }
    return peak;
  });
};
